import {useCallback, useEffect, useMemo, useRef, useState, type RefObject} from "react";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import type {DataEditorRef} from "@glideapps/glide-data-grid";
import {ActiveShiftFocusNotice, OtsThemeRoot, type DataGridFocusResult} from "@ots-frontend/ots-ui-tw";
import {setAppDate} from "../store/ui/actions";
import {clearActiveShiftFocus, readActiveShiftFocus} from "./active-shift-navigation";

/** URL-scoped highlight only: never writes saved filters, sort, columns, selection or view preferences. */
export function useActiveShiftFocus(
  regionId: string | undefined,
  editor: RefObject<DataEditorRef | null>,
  loading: boolean,
  listView = false
) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const focus = useMemo(() => readActiveShiftFocus(location.search, regionId), [location.search, regionId]);
  const [resolved, setResolved] = useState<DataGridFocusResult & {key: string}>();
  const scrolled = useRef<string | undefined>(undefined);
  const focusKey = `${focus.personId ?? ""}:${focus.date ?? ""}`;
  useEffect(() => {
    if (focus.personId && focus.date) dispatch(setAppDate(focus.date));
  }, [focus.personId, focus.date, dispatch]);
  const onResolved = useCallback(
    (result: DataGridFocusResult) => {
      setResolved((previous) =>
        previous?.key === focusKey && previous.count === result.count && previous.firstRow === result.firstRow
          ? previous
          : {...result, key: focusKey}
      );
      const key = `${focusKey}:${result.firstRow}`;
      if (result.firstRow != null && scrolled.current !== key) {
        scrolled.current = key;
        editor.current?.scrollTo(0, result.firstRow, "both", 0, 0, {vAlign: "center"});
      }
    },
    [editor, focusKey]
  );
  return {
    personId: focus.personId,
    onResolved,
    notice: focus.personId ? (
      <OtsThemeRoot className="min-w-0 shrink-0">
        <ActiveShiftFocusNotice
          listView={listView}
          driver={focus.name}
          count={resolved?.key === focusKey ? resolved.count : undefined}
          loading={loading}
          onDismiss={() =>
            navigate({pathname: location.pathname, search: clearActiveShiftFocus(location.search)}, {replace: true})
          }
        />
      </OtsThemeRoot>
    ) : null,
  };
}
